import axios from 'axios';
import {
	CloneRecipeResponse,
	SaveRecipeRequestBody,
	SaveRecipeResponse,
	UpdateRecipeResponse,
	RecipeDbEntity,
	RecipeLimitedPublicInfo,
	SharedWithUser,
	SaveRecipeRequestInfo,
	GetRecipeResponse,
	PatchRecipeDetailsRequestBody,
} from '@kemu-io/kemu-types';

export type RecipeSearchResult = {
	documents: RecipeDbEntity[];
	total: number;
	page: number;
	pages: number;
};
import { TransferProgressEvent } from '@kemu-io/kemu-core/types';
import globals from '@common/globals';
import { clerkApiInterceptor } from '../authenticatedApi/clerkApiInterceptor';


const API_NAME = globals.PLATFORM_API_NAME;
// Forces to return the entire Axios response object instead of only response.data
const returnAxiosResponse = { response: true };

// type BaseRecipeWithoutVersion = Omit<RecipeBaseEntity, 'version'>;

/**
 * Gets the recipe data from the database
 * @param config the recipe id and optional version to fetch
 * @returns the recipe data including contents and metadata
 */
const getRecipeById = async (config: { id: string; version?: number }): Promise<GetRecipeResponse> => {
	const { id, version } = config;
	const query = typeof version === 'number' ? `?version=${version}` : '';
	const recipe = await clerkApiInterceptor.get(API_NAME, `/v1/recipe/${id}${query}`, returnAxiosResponse);
	return recipe.data;
};

// export interface SaveRecipeResponse {
// 	recipeId: string;
// 	uploadUrl?: string;
// }


/**
 * Store a new recipe info in the DB. 
 * IMPORTANT: This is just part of the process, you need to upload the contents and storage files
 * after the recipe info has been added to the DB.
 * @param recipeData the raw recipe to upload
 * @param userId
 * @param storageChecksum the sha1 of the storage drive or 0 if none exists
 * @returns a pre-signed url to upload the actual recipe contents.
 */
const saveRecipe = async (config: {
	recipeData: SaveRecipeRequestInfo, 
	rawContents: string,
	contentLength: number, 
	storageSize: number, 
	storageChecksum: string
}): Promise<SaveRecipeResponse> => {
	const { recipeData, rawContents, contentLength, storageSize, storageChecksum } = config;

	const requestBody: SaveRecipeRequestBody = {
		rawContents,
		recipe: {
			...recipeData,
			contentLength,
		},
		storageSize,
		storageChecksum
	};

	const response = await clerkApiInterceptor.post(API_NAME, `/v1/recipe`, { ...returnAxiosResponse, body: requestBody });
	return response.data as SaveRecipeResponse;
};


/**
 * Updates an existing recipe
 * @param recipeInfo 
 * @param recipeId the id of the recipe to be updated.
 * @param storageChecksum the sha1 of the storage drive or 0 if none exists
 * @returns the new id of the recipe in the database
 */
const updateRecipe = async (config: {
	recipeInfo: SaveRecipeRequestInfo, 
	rawContents: string,
	recipeId: string, 
	contentLength: number, 
	storageSize: number, 
	storageChecksum: string,
	currentVersion: number
}): Promise<UpdateRecipeResponse> => {
	const { recipeInfo, rawContents, recipeId, contentLength, storageSize, storageChecksum, currentVersion } = config;

	const requestBody: SaveRecipeRequestBody = {
		rawContents,
		recipe: {
			...recipeInfo,
			contentLength,
		},
		storageSize,
		storageChecksum,
		currentVersion
	};

	const response = await clerkApiInterceptor.put(API_NAME, `/v1/recipe/${recipeId}`, { ...returnAxiosResponse, body: requestBody });
	return response.data as UpdateRecipeResponse;
};


const deleteRecipe = async (recipeId: string): Promise<void> => {
	await clerkApiInterceptor.delete(API_NAME, `/v1/recipe/${recipeId}`, returnAxiosResponse);
};

/**
 * Downloads a file with progress. 
 * @param presignedUrl a pre-signed S3 url
 * @param onProgressCb a method to call with the progress events of the download
 * @returns the recipe in JSON format
 */
const downloadRecipeFile = async <T>(
	presignedUrl: string,
	responseType: 'json' | 'arraybuffer',
	onProgressCb?: (evt: TransferProgressEvent) => void
): Promise<T> => {
	const results = await axios.get(`${presignedUrl}`, {
		responseType,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		onDownloadProgress: (evt: any) => {
			if (onProgressCb) {
				const percentage = Math.round((evt.loaded / evt.total) * 100);
				onProgressCb({ ...evt, percentage });
			}
		}
	});

	return results.data;
};


/**
 * Uploads a recipe file to S3
 * @param presignedUrl a pre-signed URL for the upload
 * @param onProgressCb a method to call with the progress events of the upload
 */
const uploadRecipeFile = async (
	presignedUrl: string,
	data: ArrayBuffer,
	onProgressCb?: (evt: TransferProgressEvent) => void
): Promise<void> => {
	const results = await axios.put(`${presignedUrl}`, data, {
		// NOTE: this MUST match the content type set by the service when creating the pre-signed url
		headers: { 'Content-Type': 'application/octet-stream' },
		onUploadProgress: (evt) => {
			if (onProgressCb) {
				const total = evt.total || 1;
				const percentage = Math.round((evt.loaded / total) * 100);
				onProgressCb({
					loaded: evt.loaded,
					percentage,
					total,
				});
			}
		}
	});

	return results.data;
};

// TODO: how to reply if the server fails because user doesn't exist
const addSharedUser = async (recipeId: string, userEmail: string | undefined, accessMode: string | undefined, notificationMessage?: string,  addIfNoneExists=false): Promise<SharedWithUser[]> => {
	const response = await clerkApiInterceptor.post(API_NAME, `/recipe/${recipeId}/addUser`, {
		...returnAxiosResponse,
		body: {
			userEmail,
			accessMode,
			notificationMessage ,
			addIfNoneExists
		}
	});

	return response.data;
};

const removeSharedUser = async (recipeId: string, userEmail: string ): Promise<SharedWithUser[]> => {
	const response = await clerkApiInterceptor.post(API_NAME, `/recipe/${recipeId}/removeUser`, {
		...returnAxiosResponse,
		body: {
			userEmail,
		}
	});

	const remainingList = response.data;
	return remainingList;
};

/**
 * Enables/Disables link sharing.
 * @param recipeId 
 * @param enable 
 */
const toggleLinkShare = async (recipeId: string, enable: boolean): Promise<string> => {
	const response = await clerkApiInterceptor.post(API_NAME, `/recipe/${recipeId}/linkShare`, {
		...returnAxiosResponse,
		body: {
			linkSharing: enable
		}
	});

	return response.data?.publicSharedId;
};

const searchRecipes = async (params: { query?: string; page?: number; perPage?: number }): Promise<RecipeSearchResult> => {
	const { query = '', page = 1, perPage = 11 } = params;
	const qs = new URLSearchParams({ q: query, page: String(page), perPage: String(perPage) }).toString();
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/recipes/search?${qs}`, returnAxiosResponse);
	return response.data as RecipeSearchResult;
};

const patchRecipe = async (recipeId: string, details: PatchRecipeDetailsRequestBody): Promise<RecipeDbEntity> => {
	const response = await clerkApiInterceptor.patch(API_NAME, `/v1/recipe/${recipeId}`, { ...returnAxiosResponse, body: details });
	return response.data as RecipeDbEntity;
};

/**
 * Creates a copy of the given recipe
 * @param recipeId 
 */
const cloneRecipe = async (recipeId: string, name?: string): Promise<RecipeDbEntity> => {
	const request: Record<string, unknown> = { ...returnAxiosResponse };
	if (name) {
		request.body = { name };
	}
	const response = await clerkApiInterceptor.post(API_NAME, `/recipe/${recipeId}/clone`, request);
	return response.data as RecipeDbEntity;
};

const getPublicRecipeInfo = async (publicId: string): Promise<RecipeLimitedPublicInfo> => {
	const recipe = await clerkApiInterceptor.get(API_NAME, `/recipe/public/${publicId}`, returnAxiosResponse);
	return recipe.data as RecipeLimitedPublicInfo;
};

const importPublicRecipe = async (publicId: string, name?: string): Promise<CloneRecipeResponse> => {
	const request: Record<string, unknown> = { ...returnAxiosResponse };
	if (name) {
		request.body = { name };
	}
	const response = await clerkApiInterceptor.post(API_NAME, `/recipe/${publicId}/import`, request);
	return response.data as CloneRecipeResponse;
};

const recipeApi = {
	getRecipeById,
	saveRecipe,
	updateRecipe,
	deleteRecipe,
	downloadRecipeFile,
	uploadRecipeFile,
	addSharedUser,
	toggleLinkShare,
	removeSharedUser,
	searchRecipes,
	patchRecipe,
	cloneRecipe,
	importPublicRecipe,
	getPublicRecipeInfo
};

export default recipeApi;